import { useState, useEffect } from "react";
import {
  Download,
  Copy,
  Check,
  FileText,
  Code2,
  Sparkles,
  BarChart2,
  HelpCircle,
  X,
  Loader2,
  Layers,
  BookOpen,
} from "lucide-react";
import { api, ApiError } from "../../lib/api";
import type { ManualExportPacketResponse } from "../../lib/api";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";

interface AdminManualPacketModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceId: string;
  chapterId: string;
  chapterTitle: string;
  topics: Array<{ id?: string; title: string }>;
  token: string;
}

export function AdminManualPacketModal({
  isOpen,
  onClose,
  resourceId,
  chapterId,
  chapterTitle,
  topics,
  token,
}: AdminManualPacketModalProps) {
  const [selectedTopicId, setSelectedTopicId] = useState<string>("");
  const [questionCount, setQuestionCount] = useState<number>(10);
  const [easyPct, setEasyPct] = useState<number>(20);
  const [mediumPct, setMediumPct] = useState<number>(60);
  const [hardPct, setHardPct] = useState<number>(20);
  const [language, setLanguage] = useState<string>("en");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [packet, setPacket] = useState<ManualExportPacketResponse | null>(null);

  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"prompt" | "quiz" | "content" | "notes">("prompt");

  useEffect(() => {
    if (isOpen && resourceId && chapterId && token) {
      loadPacket();
    } else {
      setPacket(null);
      setError(null);
    }
  }, [isOpen, resourceId, chapterId, token]);

  const loadPacket = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.admin.exportManualPacket(token, resourceId, chapterId, {
        topicId: selectedTopicId || null,
        questionCount,
        easyPct,
        mediumPct,
        hardPct,
        language,
      });
      setPacket(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to generate export packet");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, sectionKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(sectionKey);
      setTimeout(() => setCopiedSection(null), 2500);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  const downloadFile = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      showCloseButton={false}
      className="p-0 overflow-hidden max-w-4xl max-h-[90vh]"
    >
      <div className="w-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-center justify-between bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xs bg-primary/10 text-primary flex items-center justify-center font-bold">
              <Layers size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Manual Mode Export Packet</h2>
              <p className="text-xs text-muted-foreground truncate max-w-md">
                Chapter: <span className="font-semibold text-foreground">{chapterTitle}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 text-sm">
          {error && (
            <div className="p-3.5 bg-destructive/10 border border-destructive/30 text-destructive rounded-xl text-xs flex items-center justify-between">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} className="underline ml-2">Dismiss</button>
            </div>
          )}

          {/* Configuration Inputs */}
          <div className="p-4 rounded-xl border border-border/80 bg-background/50 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Sparkles size={14} className="text-primary" /> Packet Generation Parameters
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-foreground block mb-1.5">
                  Target Topic (optional)
                </label>
                <select
                  value={selectedTopicId}
                  onChange={(e) => setSelectedTopicId(e.target.value)}
                  className="w-full text-xs px-3 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">All topics in chapter</option>
                  {topics.map((t) => (
                    <option key={t.id || t.title} value={t.id || ""}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1.5">
                  Target Question Count
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={questionCount}
                  onChange={(e) => setQuestionCount(Math.max(1, parseInt(e.target.value, 10) || 10))}
                  className="w-full text-xs px-3 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1.5">
                  Language
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full text-xs px-3 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="en">English (en)</option>
                  <option value="am">Amharic (am)</option>
                  <option value="oro">Afaan Oromoo (oro)</option>
                </select>
              </div>
            </div>

            {/* Difficulty Mix Inputs */}
            <div>
              <label className="text-xs font-semibold text-foreground block mb-1.5">
                Difficulty Mix Breakdown
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
                <div className="flex items-center gap-2 bg-muted/40 p-2 rounded-lg border border-border/50 text-xs">
                  <span className="font-semibold text-success">Easy %</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={easyPct}
                    onChange={(e) => setEasyPct(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    className="w-16 px-2 py-1 rounded border border-border text-center bg-background text-xs"
                  />
                </div>
                <div className="flex items-center gap-2 bg-muted/40 p-2 rounded-lg border border-border/50 text-xs">
                  <span className="font-semibold text-warning">Med %</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={mediumPct}
                    onChange={(e) => setMediumPct(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    className="w-16 px-2 py-1 rounded border border-border text-center bg-background text-xs"
                  />
                </div>
                <div className="flex items-center gap-2 bg-muted/40 p-2 rounded-lg border border-border/50 text-xs">
                  <span className="font-semibold text-destructive">Hard %</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={hardPct}
                    onChange={(e) => setHardPct(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    className="w-16 px-2 py-1 rounded border border-border text-center bg-background text-xs"
                  />
                </div>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <Button
                type="button"
                onClick={loadPacket}
                loading={loading}
                loadingText="Generating Packet..."
                variant="primary"
                size="sm"
                icon={<Sparkles size={14} />}
              >
                Refresh Packet with Parameters
              </Button>
            </div>
          </div>

          {/* Packet Summary & Actions */}
          {packet && (
            <div className="space-y-4">
              {/* Badges bar */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="px-2.5 py-1 rounded-md bg-primary/10 text-primary font-medium border border-primary/20">
                  Profile: {packet.metadata.subjectProfile}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-muted text-muted-foreground font-mono">
                  Course ID: {packet.metadata.courseId || "N/A"}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-muted text-muted-foreground font-mono">
                  Chapter ID: {packet.metadata.chapterId}
                </span>
                {packet.hasFigures && (
                  <span className="px-2.5 py-1 rounded-md bg-info/10 text-info border border-info/30 flex items-center gap-1">
                    <BarChart2 size={13} /> {packet.figureCount} Figures Ready
                  </span>
                )}
                <span className="px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground">
                  Mix: {packet.requestedCounts.easy}E / {packet.requestedCounts.medium}M / {packet.requestedCounts.hard}H
                </span>
              </div>

              {/* Action buttons grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                <button
                  type="button"
                  onClick={() => copyToClipboard(packet.llmPrompt, "prompt")}
                  className="px-3 py-2.5 rounded-xl border border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary font-semibold text-xs flex items-center justify-center gap-1.5 transition-all shadow-sm"
                >
                  {copiedSection === "prompt" ? <Check size={14} /> : <Copy size={14} />}
                  {copiedSection === "prompt" ? "Copied Prompt!" : "Copy LLM Prompt"}
                </button>

                <button
                  type="button"
                  onClick={() => copyToClipboard(JSON.stringify(packet.templates.quiz, null, 2), "quiz")}
                  className="px-3 py-2.5 rounded-xl border border-border hover:bg-muted text-foreground font-medium text-xs flex items-center justify-center gap-1.5 transition-all"
                >
                  {copiedSection === "quiz" ? <Check size={14} /> : <Code2 size={14} />}
                  {copiedSection === "quiz" ? "Copied Quiz!" : "Copy Quiz Template"}
                </button>

                <button
                  type="button"
                  onClick={() => copyToClipboard(JSON.stringify(packet.templates.content, null, 2), "content")}
                  className="px-3 py-2.5 rounded-xl border border-border hover:bg-muted text-foreground font-medium text-xs flex items-center justify-center gap-1.5 transition-all"
                >
                  {copiedSection === "content" ? <Check size={14} /> : <BookOpen size={14} />}
                  {copiedSection === "content" ? "Copied Content!" : "Copy Content JSON"}
                </button>

                <button
                  type="button"
                  onClick={() => downloadFile(JSON.stringify(packet, null, 2), `manual_packet_${chapterTitle.replace(/[^a-zA-Z0-9_-]/g, "_")}.json`, "application/json")}
                  className="px-3 py-2.5 rounded-xl border border-border hover:bg-muted text-foreground font-medium text-xs flex items-center justify-center gap-1.5 transition-all"
                >
                  <Download size={14} /> Download Packet (.json)
                </button>
              </div>

              {/* Preview Tabs */}
              <div className="border border-border rounded-xl overflow-hidden bg-background">
                <div className="flex border-b border-border bg-muted/40 text-xs overflow-x-auto">
                  <button
                    type="button"
                    onClick={() => setActiveTab("prompt")}
                    className={`px-4 py-2.5 font-semibold transition-colors flex items-center gap-1.5 border-b-2 whitespace-nowrap shrink-0 ${
                      activeTab === "prompt"
                        ? "border-primary text-primary bg-background"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <FileText size={14} /> Ready LLM Prompt
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("quiz")}
                    className={`px-4 py-2.5 font-semibold transition-colors flex items-center gap-1.5 border-b-2 whitespace-nowrap shrink-0 ${
                      activeTab === "quiz"
                        ? "border-primary text-primary bg-background"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Code2 size={14} /> Quiz Template JSON
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("content")}
                    className={`px-4 py-2.5 font-semibold transition-colors flex items-center gap-1.5 border-b-2 whitespace-nowrap shrink-0 ${
                      activeTab === "content"
                        ? "border-primary text-primary bg-background"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <BookOpen size={14} /> Content & Figures
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("notes")}
                    className={`px-4 py-2.5 font-semibold transition-colors flex items-center gap-1.5 border-b-2 whitespace-nowrap shrink-0 ${
                      activeTab === "notes"
                        ? "border-primary text-primary bg-background"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Layers size={14} /> Notes Template
                  </button>
                </div>

                <div className="p-4 font-mono text-xs whitespace-pre-wrap break-words text-foreground bg-muted/20">
                  {activeTab === "prompt" && packet.llmPrompt}
                  {activeTab === "quiz" && JSON.stringify(packet.templates.quiz, null, 2)}
                  {activeTab === "content" && JSON.stringify(packet.templates.content, null, 2)}
                  {activeTab === "notes" && JSON.stringify(packet.templates.notes, null, 2)}
                </div>
              </div>
            </div>
          )}

          {loading && !packet && (
            <div className="py-12 flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <Loader2 size={24} className="animate-spin text-primary" />
              <p className="text-xs">Preparing export packet and templates...</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex items-center justify-between bg-muted/20">
          <p className="text-xs text-muted-foreground">
            Contains pre-filled IDs & schemas for valid import without opening the app again.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-muted"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
